import React from 'react'

/**
 * NHL UI primitives — a self-contained copy of the NFL/MLB design system (tokens,
 * tier badges, nav, cards) so the NHL app is visually identical without importing
 * from or modifying the MLB/NFL code. Kept isolated per the "don't interfere"
 * rule.
 */
export const T = {
  bg:"#080C12", card:"#111827", card2:"#1A2130", card3:"#0C1F18",
  border:"#1E2A3A", border2:"#2A3548",
  green:"#00D97E", greenDim:"#00D97E18", greenDk:"#064E3B", greenMid:"#065F46", greenLt:"#DCFCE7",
  mint:"#34D399",
  amber:"#F59E0B", amberDim:"#F59E0B18",
  red:"#EF4444", redDim:"#EF444418",
  blue:"#3B82F6", blueDim:"#3B82F618", blueLt:"#1E3A5F",
  ice:"#38BDF8", iceDim:"#38BDF818",
  orange:"#F97316", purple:"#8B5CF6",
  text:"#F1F5F9", muted:"#A6B2C4", muted2:"#CBD5E1", faint:"#475569",
  mono:"'SF Mono','Fira Code',monospace",
}

export const TIER = {
  "ELITE":     {bg:"#DBEAFE", fg:"#1E3A8A", bd:"#2563EB", icon:"◆",  label:"ELITE"},
  "BEST BET":  {bg:"#DBEAFE", fg:"#1E40AF", bd:"#3B82F6", icon:"🔥", label:"TOP RATED"},
  "STRONG":    {bg:"#DCFCE7", fg:"#005C3A", bd:"#00D97E", icon:"✅", label:"STRONG"},
  "PLAY":      {bg:"#D1FAE5", fg:"#065F46", bd:"#34D399", icon:"👀", label:"MODERATE"},
  "WATCH":     {bg:"#FEF3C7", fg:"#7C3B0A", bd:"#F59E0B", icon:"👁", label:"WATCH"},
  "PASS":      {bg:"#FEE2E2", fg:"#7F1D1D", bd:"#EF4444", icon:"✕",  label:"LOW"},
}
export const RECO = new Set(["ELITE", "BEST BET", "STRONG"])

// ── book names + odds formatting (line-shopping) ────────────────────────────
export const BOOK_NAMES = {
  draftkings:"DraftKings", fanduel:"FanDuel", betmgm:"BetMGM", caesars:"Caesars",
  espnbet:"ESPN Bet", fanatics:"Fanatics", betrivers:"BetRivers", pointsbetus:"PointsBet",
  hardrockbet:"Hard Rock", ballybet:"Bally Bet", bovada:"Bovada", betonlineag:"BetOnline",
  williamhill_us:"Caesars", wynnbet:"WynnBET", superbook:"SuperBook", unibet_us:"Unibet",
}
export const bookName = k => BOOK_NAMES[k] || (k ? k.replace(/_/g,' ').replace(/\b\w/g, c=>c.toUpperCase()) : 'Book')
export const fmtOdds = o => o==null ? '—' : (o>0 ? `+${o}` : `${o}`)

export const pct = v => `${Math.round((v||0)*100)}%`
// Low-base-rate markets (P(>=1) at a 0.5 line) are naturally well under 50%, so the
// probability color must track the market's own scale — otherwise a green STRONG
// badge sits next to a red % on the same card. Thresholds mirror backend tier_scales.
const _PROB_COLOR = {
  goals:     [0.40, 0.32, 0.24], assists: [0.38, 0.30, 0.22],
  pp_points: [0.30, 0.22, 0.14], points:  [0.50, 0.42, 0.34],
}
export const probColor = (p, market) => {
  const [g, m, a] = _PROB_COLOR[market] || [0.65, 0.55, 0.45]
  return p>=g?T.green : p>=m?"#34D399" : p>=a?T.amber : T.red
}
// Shared tier ordering (best first) so mixed-market lists rank by conviction, not
// by raw probability (which would always sink the low-base markets).
export const TIER_RANK = {"ELITE":0,"BEST BET":1,"STRONG":2,"PLAY":3,"WATCH":4,"PASS":5}
export const tierRank = t => TIER_RANK[t] ?? 9
export const confColor = c => c>=85?T.blue : c>=70?T.green : c>=55?"#34D399" : c>=42?T.amber : T.red
export const edgeColor = e => e==null?T.muted : e>=3?T.green : e>=0?"#34D399" : T.red

// Worded verdict chip — the NFL "The Case" look (same levels, same words).
export const VLEVEL = {
  strong:  {c:"#04140C", b:T.green,       bd:T.green},
  good:    {c:T.green,   b:"transparent", bd:T.greenMid},
  neutral: {c:T.amber,   b:"transparent", bd:`${T.amber}66`},
  warn:    {c:T.red,     b:"transparent", bd:`${T.red}66`},
  dead:    {c:T.muted,   b:"transparent", bd:T.border},
}
export const BET_VLEVEL = {"Strong":"strong","Leans yes":"good","Mixed":"neutral","Toss-up":"neutral",
  "Tough":"warn","Leans no":"warn","Priced out":"dead"}
export function VerdictChip({label, level="neutral", sub, size=10, title}){
  const s = VLEVEL[level] || VLEVEL.neutral
  return (
    <span title={title} style={{fontSize:size,fontWeight:800,letterSpacing:"0.04em",color:s.c,background:s.b,
      border:`1px solid ${s.bd}`,borderRadius:6,padding:"2px 7px",whiteSpace:"nowrap",display:"inline-block"}}>
      {label}{sub?<span style={{fontWeight:700,opacity:0.85}}> · {sub}</span>:null}
    </span>
  )
}

// Market label + canonical unit for the board selector.
export const NHL_MARKETS = [
  { key: 'shots',     label: 'Shots' },
  { key: 'points',    label: 'Points' },
  { key: 'goals',     label: 'Anytime Goal' },
  { key: 'saves',     label: 'Goalie Saves' },
  { key: 'assists',   label: 'Assists' },
  { key: 'blocks',    label: 'Blocks' },
  { key: 'pp_points', label: 'PP Points' },
]
export const MARKET_LABEL = Object.fromEntries(NHL_MARKETS.map(m => [m.key, m.label]))

// Top tiers require CONFIDENCE, not just a high probability (mirrors backend rating.tier;
// the API already returns the authoritative `tier`, this is only a client fallback).
export const tierFromProb = (p, conf) =>
  conf>=85 && p>=0.62 ? "ELITE" : conf>=68 && p>=0.66 ? "BEST BET" : conf>=58 && p>=0.60 ? "STRONG" : p>=0.52 ? "PLAY" : p>=0.45 ? "WATCH" : "PASS"

export const RATING_LETTER = {"ELITE":"A+","BEST BET":"A","STRONG":"A-","PLAY":"B","WATCH":"C+","PASS":"C"}
export const ratingColor = r => r && r[0]==="A" ? T.green : r && r[0]==="B" ? T.amber : T.muted2
export function RatingBadge({tier,size=34}){
  const r = RATING_LETTER[tier] || "C"
  const c = ratingColor(r)
  return <span title={`Rating ${r}`} style={{flex:"none",width:size,height:size,borderRadius:10,
    display:"grid",placeItems:"center",fontFamily:T.mono,fontWeight:800,fontSize:Math.round(size*0.44),
    letterSpacing:"-0.03em",color:c,background:`${c}1A`,border:`1px solid ${c}55`}}>{r}</span>
}

export function TierBadge({tier}){
  const t = TIER[tier]||TIER.WATCH
  return <span style={{fontSize:9,fontWeight:800,color:t.fg,background:t.bg,border:`1px solid ${t.bd}`,borderRadius:20,padding:"3px 9px",letterSpacing:"0.06em",whiteSpace:"nowrap"}}>{t.icon} {t.label}</span>
}

export function Avatar({url, name, size=38}){
  const [err,setErr] = React.useState(false)
  const initials = (name||'').split(' ').filter(Boolean).map(w=>w[0]).slice(0,2).join('').toUpperCase()
  const box = {width:size,height:size,borderRadius:"50%",flexShrink:0,overflow:"hidden",
    background:T.card2,border:`1px solid ${T.border2}`,display:"flex",alignItems:"center",justifyContent:"center"}
  if(url && !err) return (
    <div style={box}><img src={url} alt="" width={size} height={size} loading="lazy"
      onError={()=>setErr(true)} style={{width:"100%",height:"100%",objectFit:"cover"}}/></div>
  )
  return <div style={{...box,fontSize:size*0.34,fontWeight:800,color:T.muted}}>{initials||"—"}</div>
}

// Shown while the board is still leaning on last season's data (opening weeks),
// so users know the numbers are carryover until 2026-27 games accumulate. The
// backend flag flips off on its own as players cross onto current-season data.
export function CarryoverChip(){
  return (
    <div style={{display:"flex",alignItems:"flex-start",gap:7,background:T.iceDim,
      border:`1px solid ${T.ice}44`,borderRadius:10,padding:"8px 11px",margin:"0 0 10px"}}>
      <span style={{fontSize:13,lineHeight:1.2}}>📅</span>
      <div style={{fontSize:10.5,color:T.muted2,lineHeight:1.45}}>
        <b style={{color:T.ice}}>Early season</b> — projections lean on last season's
        data until each player logs ~10 games this year. Numbers sharpen game by game.
      </div>
    </div>
  )
}

export function Stat({label,value,color=T.text,bg=T.card2}){
  return (
    <div style={{background:bg,border:`1px solid ${T.border2}`,borderRadius:8,padding:"8px 6px",textAlign:"center"}}>
      <div style={{fontSize:16,fontWeight:800,color,fontFamily:T.mono,lineHeight:1.1}}>{value}</div>
      <div style={{fontSize:8.5,color:T.muted,letterSpacing:"0.1em",marginTop:3,textTransform:"uppercase"}}>{label}</div>
    </div>
  )
}

export function SectionLabel({children, right, onRight}){
  return (
    <div style={{display:"flex",alignItems:"center",justifyContent:"space-between",margin:"18px 0 10px"}}>
      <span style={{fontSize:12,fontWeight:800,color:T.muted,letterSpacing:"0.08em",textTransform:"uppercase"}}>{children}</span>
      {right && <button onClick={onRight} style={{background:"none",border:"none",color:T.green,fontSize:11,fontWeight:700,cursor:onRight?"pointer":"default"}}>{right}</button>}
    </div>
  )
}

export function Spinner(){
  return (
    <div style={{display:"flex",justifyContent:"center",padding:40}}>
      <div style={{width:30,height:30,borderRadius:"50%",border:`3px solid ${T.border}`,borderTopColor:T.green,animation:"spin 0.8s linear infinite"}}/>
      <style>{`@keyframes spin{to{transform:rotate(360deg)}}`}</style>
    </div>
  )
}

export function Empty({message="No data available"}){
  return <div style={{textAlign:"center",padding:"40px 16px",color:T.muted}}><div style={{fontSize:26,marginBottom:8}}>🏒</div><div style={{fontSize:13,fontWeight:600}}>{message}</div></div>
}

export function SearchBar({value, onChange, placeholder="Search player or team…", style}){
  return (
    <div style={{position:"relative", marginBottom:8, ...style}}>
      <input value={value} onChange={e=>onChange(e.target.value)} placeholder={placeholder}
        style={{width:"100%", boxSizing:"border-box", padding:"9px 30px 9px 12px", borderRadius:9,
          background:T.card, color:T.text, border:`1px solid ${value?T.green:T.border}`, fontSize:13, outline:"none"}} />
      {value && <button onClick={()=>onChange("")} aria-label="Clear search"
        style={{position:"absolute", right:8, top:"50%", transform:"translateY(-50%)", background:"none",
          border:"none", color:T.muted, cursor:"pointer", fontSize:15, lineHeight:1}}>×</button>}
    </div>
  )
}
export const searchMatch = (q, fields) => {
  const ql = (q||"").trim().toLowerCase()
  if(!ql) return () => true
  return item => (fields(item)||[]).filter(Boolean).join(" ").toLowerCase().includes(ql)
}

export function Tabs({tabs,active,onSelect}){
  return (
    <div style={{display:"flex",gap:2,borderBottom:`1px solid ${T.border}`,marginBottom:14,overflowX:"auto",scrollbarWidth:"none"}}>
      {tabs.map(t=>{ const on=active===t; return (
        <button key={t} onClick={()=>onSelect(t)} style={{flexShrink:0,padding:"9px 12px",border:"none",background:"transparent",color:on?T.green:T.muted,fontSize:12,fontWeight:on?800:600,cursor:"pointer",borderBottom:`2px solid ${on?T.green:"transparent"}`,marginBottom:-1}}>{t}</button>
      )})}
    </div>
  )
}

function NavIcon({type,color}){
  const c={fill:"none",stroke:color,strokeWidth:2,strokeLinecap:"round",strokeLinejoin:"round"}
  if(type==="home") return <svg width="20" height="20" viewBox="0 0 24 24" {...c}><path d="M3 11l9-8 9 8"/><path d="M5 10v10h5v-6h4v6h5V10"/></svg>
  if(type==="board") return <svg width="20" height="20" viewBox="0 0 24 24" {...c}><path d="M12 2l2.4 6.5H21l-5.3 4 2 6.5L12 15l-5.7 4 2-6.5L3 8.5h6.6z"/></svg>
  if(type==="games") return <svg width="20" height="20" viewBox="0 0 24 24" {...c}><path d="M12 3l9 9-9 9-9-9z"/><path d="M9 12l2 2 4-4"/></svg>
  if(type==="record") return <svg width="20" height="20" viewBox="0 0 24 24" {...c}><path d="M3 3v18h18"/><path d="M7 14l4-4 3 3 5-6"/></svg>
  if(type==="spots") return <svg width="20" height="20" viewBox="0 0 24 24" {...c}><circle cx="12" cy="12" r="9"/><circle cx="12" cy="12" r="5"/><circle cx="12" cy="12" r="1" fill={color}/></svg>
  return <svg width="20" height="20" viewBox="0 0 24 24" {...c}><circle cx="12" cy="12" r="9"/><path d="M12 7v5l3 2"/></svg>
}

export function BottomNav({view,setView}){
  const items=[["home","Home"],["board","Board"],["games","Games"],["spots","Spots"],["record","Record"]]
  return (
    <nav style={{position:"fixed",bottom:0,left:0,right:0,zIndex:200,maxWidth:460,margin:"0 auto",background:"#0D1117",borderTop:`1px solid ${T.border}`,display:"flex",paddingBottom:"env(safe-area-inset-bottom,0)"}}>
      {items.map(([id,label])=>{ const on=view===id; const col=on?T.green:T.muted; return (
        <button key={id} onClick={()=>setView(id)} style={{flex:1,display:"flex",flexDirection:"column",alignItems:"center",gap:3,padding:"9px 4px 7px",border:"none",background:"none",cursor:"pointer",color:col,fontSize:10,fontWeight:700,letterSpacing:"0.04em",position:"relative"}}>
          {on&&<div style={{position:"absolute",top:0,left:"50%",transform:"translateX(-50%)",width:26,height:2,background:T.green,borderRadius:"0 0 4px 4px"}}/>}
          <NavIcon type={id} color={col}/>{label}
        </button>)})}
    </nav>
  )
}
